import logger from '@/lib/clientLogger';
import getConfig from 'next/config';
import { GoogleTagManagerInput } from '@/__generated__/globalTypes';
import { GtmData, GtmServiceBase, GtmFields } from '@care/google-tag-manager';
import { AnalyticsHelper } from './analyticsHelper';

const {
  publicRuntimeConfig: { GA_MEASUREMENT_ID },
} = getConfig() as Record<string, Record<string, string>>;
type ProviderStart = GtmFields.Member &
  GtmFields.Service &
  GtmFields.MemberDetails & { zipCode?: string };
type SeekerJobPost = GtmFields.Member &
  GtmFields.Service & { jobVertical?: string; jobType: string };
class GtmServiceExtended extends GtmServiceBase {
  /**
   * Retrieves the Google Tag Manager input values, including the Google Analytics 4 client ID and
   * session ID, if available.
   *
   * @returns A Promise that resolves to the Google Tag Manager input values, or null if the values
   * are not available or the time limit is exceeded.
   */
  getGoogleTagManagerInput = async (): Promise<GoogleTagManagerInput | null> => {
    const { dataLayer, gtag } = window;

    const getIdsPromise = new Promise<GoogleTagManagerInput | null>((resolve) => {
      const getIds = () => {
        if (
          typeof gtag !== 'undefined' &&
          Array.isArray(dataLayer) &&
          dataLayer.length > 0 &&
          dataLayer[0]['gtm.uniqueEventId']
        ) {
          // eslint-disable-next-line @typescript-eslint/no-shadow
          const getGa4ClientIdPromise = new Promise<string>((resolve) => {
            gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId: string) => {
              resolve(clientId);
            });
          });

          // eslint-disable-next-line @typescript-eslint/no-shadow
          const getGa4SessionIdPromise = new Promise<string>((resolve) => {
            gtag('get', GA_MEASUREMENT_ID, 'session_id', (sessionId: string) => {
              resolve(sessionId);
            });
          });

          Promise.all([getGa4ClientIdPromise, getGa4SessionIdPromise])
            .then(([ga4ClientId, ga4SessionId]) => {
              resolve({ ga4ClientId, ga4SessionId });
            })
            .catch((error) => {
              logger.warn({
                warningMessage: 'issue with retrieving ga4ClientId and/or ga4SessionId',
                warningObject: {
                  error: `error in getIdsPromise: ${error}`,
                },
              });

              resolve(null);
            });
        } else {
          setTimeout(getIds, 200); // Check if dataLayer is ready every 200ms
        }
      };

      getIds();
    });

    const timeLimitPromise = new Promise<GoogleTagManagerInput | null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, 2000);
    });

    const result = await Promise.race([getIdsPromise, timeLimitPromise]); // wait until getIdsPromise is resolved & if it's not - returns null after 2 seconds

    if (!result) {
      const isDataLayerArray = Array.isArray(dataLayer);

      logger.warn({
        warningMessage: 'dataLayer and/or gtag did not load',
        warningObject: {
          isGtagAvailable: typeof gtag !== 'undefined',
          isDataLayerAvailable: typeof dataLayer !== 'undefined' && isDataLayerArray,
          dataLayerLength: isDataLayerArray ? dataLayer.length : 0,
          uniqueEventId:
            isDataLayerArray && dataLayer.length > 0 ? dataLayer[0]['gtm.uniqueEventId'] : null,
        },
      });
    }

    return result;
  };

  public async queueProviderStart(data: ProviderStart) {
    await super.queueEvent({
      ...data,
      gtmEvent: 'providerStart',
      memberType: 'sitter',
      overallStatus: 'basic',
      slots: ['/us-subscription/conversion/provider/basic/incomplete/'],
    });
  }

  public async queueSeekerJobPost(data: SeekerJobPost) {
    await super.queueEvent({
      ...data,
      gtmEvent: 'seekerJobPost',
      memberType: 'seeker',
    });
  }
}

// GtmService instantiated with clientLogger
export const GtmService = new GtmServiceExtended({
  logger,
  onSuccess: (data: GtmData) => {
    AnalyticsHelper.logEvent({
      name: 'Slot Sent',
      data: { slots: data.slots },
    });
  },
});

export { GtmEvent } from '@care/google-tag-manager';
